import Link from 'next/link';
import classNames from 'classnames';
import { Flame, Tag } from 'lucide-react';
import i18n from '@/settings/i18n';
import { OfferCardProps } from './types';
import { priceFormater } from '@/settings/index';
import LazyImage from '../LazyImage';
import { priceFormatSuperscript } from '@/lib/price';

const t = i18n.init('shared');

const Badge = ({
  featured,
  bestOffer,
}: {
  featured?: boolean;
  bestOffer?: boolean;
}) => {
  if (featured) {
    return (
      <div className="absolute left-3 top-3 z-10 flex h-5 items-center justify-center gap-1 rounded-full bg-[#FEF0F1] px-2.5 text-xxs font-bold leading-none text-[#E20A45]">
        <Flame className="-mt-px h-3 w-3 fill-[#E20A45] text-[#E20A45]" />{' '}
        {t`Flash Sale`}
      </div>
    );
  }

  if (bestOffer) {
    return (
      <div className="absolute left-3 top-3 z-10 flex h-5 items-center justify-center gap-1 rounded-full bg-[#f4f4f4] px-2.5 text-xxs font-bold leading-none text-[#747777]">
        <Tag className="-mt-px h-3 w-3 text-[#747777]" strokeWidth={2.5} />
        {t`Best Offer`}
      </div>
    );
  }

  return null;
};

const OfferCard = ({
  index,
  link,
  image,
  title,
  compareAtPrice,
  percentageSaving,
  price,
  bestOffer,
  className,
  featured,
  titleTag: TitleTag = 'h3',
}: OfferCardProps) => (
  <Link
    href={link}
    className={classNames(
      'flex h-full min-h-[265px] cursor-pointer flex-col border border-gray-500 !bg-white p-4 text-left text-xs',
      className,
    )}
  >
    <Badge featured={featured} bestOffer={bestOffer} />
    <div className="relative mx-auto mb-6 mt-4 min-h-[80px] w-full max-w-[130px] lg:min-h-[80px] xl:min-h-[90px]">
      <LazyImage
        src={image}
        alt={title}
        fill
        className="object-contain"
        sizes="70px"
        loading={index === 0 ? 'eager' : 'lazy'}
      />
    </div>
    <div className="flex flex-1 flex-col justify-between">
      <TitleTag
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className="two-line-ellipsis text-[13px] font-medium leading-tight"
      >
        {title}
      </TitleTag>
      <div className="mt-6 flex flex-wrap-reverse items-end justify-between gap-x-3 gap-y-1">
        <div>
          {compareAtPrice && percentageSaving ? (
            <span className="mb-0.5 block text-xs text-gray-700/50">
              {t`new`}{' '}
              <span className="line-through" translate="no">
                {priceFormatSuperscript(priceFormater.long(compareAtPrice))}
              </span>
            </span>
          ) : null}
          <div className="flex items-center gap-2">
            <span className="text-base font-bold" translate="no">
              {priceFormatSuperscript(priceFormater.long(price))}
            </span>
            {percentageSaving && percentageSaving > 3 ? (
              <div className="flex h-6 items-center justify-center bg-[#FEF0F1] px-1.5 text-xs font-bold text-[#E20A45]">
                -{percentageSaving}%
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  </Link>
);

export default OfferCard;
